
import Model from './Model'
import Invoice from './Invoice'
import OrderContent from './OrderContent'
import OrderStatus from './Static/OrderStatus'
import MenuItemType from './Static/MenuItemType'
import PaymentMode from './Static/PaymentMode'
import TablePriceCalculator from './Services/TablePriceCalculator'

export default class Order extends Model {
  constructor (attributes = {}) {
    super(attributes.id)
    this.alreadyPaid = parseFloat(attributes.alreadyPaid ?? 0) // DOUBLE 8,2
    this.cancelReason = attributes.cancelReason ?? null // VARCHAR 255
    // this.canceled = attributes.canceled ?? null // DATETIME
    this.closed = attributes.closed ?? null // DATETIME
    // this.delivery = attributes.delivery_id ? new Delivery(attributes.delivery_id) : null // INT
    // this.delivery_id = attributes.delivery_id ?? null // INT
    this.discount = attributes.discount ?? null // VARCHAR 255 (JSON)
    this.discountAmount = parseFloat(attributes.discountAmount ?? 0) // DECIMAL 8,2
    this.guests = attributes.guests // INT
    this.lockedBy = attributes.lockedBy // VARCHAR 255
    // this.margin = parseFloat(attributes.margin) || null // DECIMAL 8,2
    // this.merged = attributes.merged ?? null // DATETIME
    // this.notes = this.notes ?? null // VARCHAR 255
    this.notes_sent = attributes.notes_sent ?? null // VARCHAR 255
    // this.offline_id = attributes.offline_id ?? null // INT
    this.opened = attributes.opened // DATETIME
    this.orderDiscountAmount = parseFloat(attributes.orderDiscountAmount ?? 0) // DECIMAL 8,2
    this.refunded_invoice_id = attributes.refunded_invoice_id ?? null // INT
    this.status = attributes.status ?? OrderStatus.NORMAL // TINYINT
    this.subtotal = parseFloat(attributes.subtotal ?? 0) // DECIMAL 8,2
    this.sum = parseFloat(attributes.sum ?? 0) // DECIMAL 8,2
    this.tableName = attributes.tableName // VARCHAR 255
    this.table_id = attributes.table_id ?? null // INT
    this.taxAmount = parseFloat(attributes.taxAmount ?? 0) // DECIMAL 8,2
    this.tenantUserName = attributes.tenantUserName // VARCHAR 255
    this.tenantUser_id = attributes.tenantUser_id // INT
    this.total = parseFloat(attributes.total ?? 0) // DECIMAL 8,2
    this.extra_attributes = Object.isObject(attributes.extra_attributes) ? attributes.extra_attributes : {} // JSON

    this.orderContents = attributes.orderContents?.map((o, index) => new OrderContent({index, ...o})) ?? []
    this.orderInvoices = attributes.orderInvoices?.map(o => new Invoice(o)) ?? []

    this.invoiceContentsLink = []
    // this.invoicesIdsToDelete = []
    this.subContentsIdsToDelete = []
    this.orderContentsIdsToDelete = []
    // this.paymentsIdsToDelete = []

    this.orderInvoices.forEach(invoice => {
      invoice.order = this
    })
  }

  get openedMoment () {
    return this._parseUtcToLocalMoment(this.opened)
  }

  get closedMoment () {
    return this.closed ? this._parseUtcToLocalMoment(this.closed) : null
  }

  get isEmpty () {
    return this.orderContents.length === 0
  }

  get amountToPay () {
    return this.total - this.alreadyPaid
  }

  get liveInvoice () {
    // A "live" invoice will hold the STANDARD and SPLIT_PERSONS payments.
    return this.orderInvoices.find(o => o.paymentMode !== PaymentMode.SPLIT_ITEMS)
  }

  get lastInvoice () {
    return this.orderInvoices.length
      ? this.orderInvoices.last()
      : null
  }

  get lines () {
    return this.orderContents.sum('lines')
  }

  get isOpen () {
    return ! this.closed
  }

  get isRefund () {
    return this.status == OrderStatus.REFUND
      || !! this.refunded_invoice_id
  }

  // get isRefunded () {
  //   return this.orderInvoices.length > 0
  //     && this.orderInvoices.every(invoice => invoice.refunded)
  // }

  get canBeRefunded () {
    return ! this.isOpen
      && ! this.isRefund
      && this.orderInvoices.some(invoice => invoice.canBeRefunded)
  }

  get leftToPay () {
    return this.total - this.alreadyPaid
  }

  get isPaid () {
    return this.leftToPay.equalTo(0) && this.orderInvoices.length > 0
  }

  getInvoice (mode) {
    return this[mode == PaymentMode.SPLIT_ITEMS ? 'lastInvoice' : 'liveInvoice']
  }

  // find (id) {
  //   return this.orderContents.find(o => o.item_id == id)
  // }

  count (id) {
    return this.orderContents.filter(o => o.item_id == id).sum('quantity')
  }

  // If the content exists inside the current order increment the counter
  // else create one (the counter will be automatically initialized to 1)
  add (attributes) {
    attributes = new TablePriceCalculator(attributes).apply()
    const item = $vm.$store.getters['catalog/item'](attributes.item_id)
    let quantity = 1
    if (!! item && item.minQuantity && item.type != MenuItemType.MENU) {
      quantity = this.minQuantityToAdd(item) || 1
    }
    for (let i = 0; i < quantity; i++) {
      this._increment(attributes) || this._push(attributes)
    }
  }

  _increment (attributes) {
    if ($vm.$store.getters['orders/config/neverStackItems']) {
      return false
    }

    const similar = this.orderContents.filter(o => o.item_id == attributes.item_id)
    if (similar.length === 0) {
      return false
    }

    const newContent = new OrderContent(attributes)
    return similar.find(content => content.isSameAs(newContent))?.increment() // REV-5412
  }

  _push (attributes) {
    attributes.index = this.orderContents.length
    this.$log('Order', `Added OrderContent [${attributes.index}]: ${attributes.itemName}`)
    this.orderContents.push(new OrderContent(attributes))
  }

  remove (index) {
    const id = this.orderContents[index].id
    if (id) {
      this.orderContentsIdsToDelete.push(id)
    }
    this.$log('Order', `Removed OrderContent [${index}]: ${id || 'NULL'}`)
    this.orderContents.splice(index, 1)
    this._reindex()
  }

  _reindex () {
    this.orderContents.forEach((content, index) => {
      content.index = index
    })
  }

  split (index, quantity, invoice) {
    const content = this.orderContents[index]
    if (content === undefined) {
      AndroidDevice.warn('Order@split: content is undefined')
      return
    }
    if (content.quantity > quantity) {
      const newContent = {...content}
      newContent.quantity = content.quantity - quantity
      delete newContent.id
      this.$log('Order', `Splitted OrderContent [${index}]: ${content.id || 'NULL'} (from ${content.quantity} to ${quantity} + ${newContent.quantity})`)
      this._push(newContent)
      content.quantity = quantity
      content.recalculate()
    }
    if (invoice) {
      content.alreadyPaidQuantity = quantity
      invoice._splitedOrderContents.push(content)
    }
  }

  unset () {
    if (this.isEmpty) {
      return
    }
    this.$log('Order', 'Empty Order')
    const ids = this.orderContents.pluck('id').filter(id => id > 0)
    this.orderContents = []
    this.orderContentsIdsToDelete = this.orderContentsIdsToDelete.concat(ids)
  }

  recalculate () {
    if (! this.orderContents.length) {
      this.discountAmount = this.orderDiscountAmount = this.subtotal = this.sum = this.taxAmount = this.total = 0
      return
    }

    this._calculateSum()
    this._calculateDiscountAmount()
    this._calculateSubtotal()
    this.total = this._calculateValueWithDiscount(this.orderContents.sum('total'))
    this.taxAmount = Math.roundToCurrency(this.total - this.subtotal)
    this._recalculateDiscountAmount()
  }

  _calculateSum () {
    this.sum = this.orderContents.sum('_priceWithExtras')
  }

  _calculateDiscountAmount () {
    const contentsDiscountAmount = this.orderContents.sum('discountAmount')
    this._calculateOrderDiscount(contentsDiscountAmount)
    this.discountAmount = Math.roundToCurrency(contentsDiscountAmount + this.orderDiscountAmount)
  }

  _calculateOrderDiscount (contentsDiscountAmount) {
    if (! this.discount) {
      this.orderDiscountAmount = 0
      return
    }
    const sum = this.sum - contentsDiscountAmount
    if (!! this.discount.isPercentage) {
      this.orderDiscountAmount = sum * this.discount.discount / 100
      return
    }
    this.orderDiscountAmount = this.discount.discount > sum
      ? sum
      : parseFloat(this.discount.discount)
  }

  _calculateSubtotal () {
    const subtotalAmount = this.orderContents.reduce((acc, { subtotal }) => {
      const factor = this.sum - (this.discountAmount - this.orderDiscountAmount)
      if (factor.equalTo(0)) {
        return acc + subtotal
      }
      const proratedContentDiscount = subtotal * this.orderDiscountAmount / factor
      return acc + subtotal - proratedContentDiscount
    }, 0)

    this.subtotal = Math.roundToCurrency(subtotalAmount)
  }

  _calculateValueWithDiscount (value) {
    if (! this.discount) {
      return this._roundAlmostZeroValue(value)
    }
    if (!! this.discount.isPercentage) {
      const valueDiscount = value * this.discount.discount / 100
      return Math.roundToCurrency(this._roundAlmostZeroValue(value - valueDiscount))
    }
    const percentage = this.discount.discount / (this.sum - this.orderContents.sum('discountAmount'))
    if (Number(value * percentage).greaterThanOrEqualTo(value)) {
      return 0
    }
    return Math.roundToCurrency(this._roundAlmostZeroValue(value - value * percentage))
  }

  _roundAlmostZeroValue (v) {
    return this._isAlmostZero(v) ? 0 : v
  }

  _isAlmostZero (v) {
    return Number(v).equalTo(0)
  }

  _recalculateDiscountAmount () {
    const sum = $vm.$store.getters['rummage/business']('taxIncluded')
      ? this.sum
      : (this.sum + this.taxAmount)
    this.discountAmount = Math.roundToCurrency(sum - this.total)
  }

  createInvoice (attributes) {
    this.orderInvoices.push(new Invoice({
      ...attributes,
      order_id: this.id,
      orderDiscountAmount: this.orderDiscountAmount,
    }))
  }

  // copied from Xef’s APP GSLinkInvoiceWithContents.m
  linkInvoicesWithContents () {
    const sortedContents = [...this.orderContents].sortBy('stringHash') // REV-19179
    this.invoiceContentsLink = this.orderInvoices
      .map(invoice => invoice._splitedOrderContents
        .flatMap(content => new Array(Math.abs(content.quantity)).fill(content))
        .map(content => {
          const index = sortedContents.findIndex(o => o === content)
          return index === -1 ? null : index
        }))
  }

  // from Xef's Controllers/Order/EnoughQuantityRule.swift
  validMinQuantity (item, add = 0) {
    if (! item.minQuantity) {
      return true
    }
    return (this.count(item.id) + add) >= item.minQuantity
  }

  invalidMinQuantity (item, add = 0) {
    return ! this.validMinQuantity(item, add)
  }

  minQuantityToAdd (item) {
    return Math.max(item.minQuantity - this.count(item.id), 0)
  }

  sendKitchenNote (id) {
    this.notes_sent = this.notes_sent === null
      ? `${id}`
      : `${this.notes_sent},${id}`
  }

  addAction(action) {
    if (! this.extra_attributes || typeof this.extra_attributes !== 'object') {
      this.extra_attributes = {}
    }
    if (! Array.isArray(this.extra_attributes.pendingHandyActions)) {
      this.extra_attributes.pendingHandyActions = []
    }
    this.extra_attributes.pendingHandyActions.push(action)

  }

  close () {
    this.closed = this._nowToUtcString()
  }

  toJSON () {
    this.orderDiscountAmount = Math.roundToCurrency(this.orderDiscountAmount)

    return this
  }
}
